import {
	DEFAULT_FINISHES,
	type FinishConfig,
} from "../domain/finishes/types";
import {
	DEFAULT_TIME_OF_DAY,
	type TimeOfDayValue,
} from "../domain/viewer/timeOfDay";
import { createDeckPreset } from "../domain/deck/deckPresets";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../domain/roof/roofConfig";
import {
	DEFAULT_SAUNA_ROTATION_DEG,
	rotateAttachWall,
	SAUNA_ROTATION_STEPS,
	type SaunaRotationDeg,
} from "../domain/viewer/saunaRotation";
import { TWO_ROOM_PANORAMA_WALL } from "../engines/twoRoomConstants";
import type {
	DeckConfig,
	RoofConfig,
	SaunaDimensions,
	TwoRoomConfig,
} from "../engines/types";

export type TwoRoomPresetId = "classic" | "lakeside" | "openPanorama";

export const TWO_ROOM_PRESETS: Record<TwoRoomPresetId, TwoRoomConfig> = {
	classic: {
		entryWall: "short",
		glazingStyle: "standard",
		saunaPosition: "left",
		hasRelaxRoom: true,
	},
	lakeside: {
		entryWall: "long",
		glazingStyle: "panorama",
		saunaPosition: "left",
		hasRelaxRoom: true,
	},
	openPanorama: {
		entryWall: "long",
		glazingStyle: "panorama",
		saunaPosition: "left",
		hasRelaxRoom: false,
	},
};

/**
 * The character a preset brings besides its layout. The panorama presets
 * are the product's showpiece — a dark facade around a wall of glass, a
 * warm wood interior, shown at blue hour when the interior glows — so
 * picking one dresses the design that way instead of leaving a glass wall
 * in bare spruce under a noon sky.
 *
 * Applied only over values the user has NOT chosen yet: a finish still at
 * its default and a time of day still at the default midday. Anyone who
 * already picked red slamfärg keeps it.
 */
export interface TwoRoomPresetLook {
	finishes: Partial<FinishConfig>;
	timeOfDay: TimeOfDayValue;
	/** Lay a deck along the panorama glass wall. */
	deckAtGlassWall: boolean;
	/** Replace the two-room's default gable roof. */
	roofConfig: RoofConfig | null;
	/**
	 * Turn the building so the glass wall faces the lake. The site keeps the
	 * lake at world +Z and the forest at -Z, so this also puts the long-wall
	 * entry (opposite the glass) toward the forest.
	 */
	glassTowardWater: boolean;
}

/** Blue hour: the sky has dimmed, the interior and the LED lines carry the scene. */
export const PANORAMA_PRESET_TIME_OF_DAY: TimeOfDayValue = 0.88;

/**
 * A low shed roof in standing-seam metal: the flat box silhouette of the
 * reference render. 8° is above the 1:10 (≈5.7°) AMA Hus minimum for
 * double-seamed band roofing, and inside the mono pitch limits.
 *
 * The shed falls ACROSS the building, long side to long side: high at the
 * panorama glass wall, low at the long entry wall, so both eaves run along
 * the long walls and the short end walls are the raked ones — the usual
 * Swedish pulpettak on a view sauna (short span, tall side toward the view).
 * `highWall` is in the building's local frame, so it turns with the
 * glass-toward-water rotation below.
 */
export const PANORAMA_PRESET_ROOF: RoofConfig = {
	kind: "mono",
	pitchDeg: 8,
	systemId: "sheet_metal",
	runoff: "free_drip",
	highWall: TWO_ROOM_PANORAMA_WALL,
};

/** The rotation that turns the panorama glass wall toward the lake (world front). */
export const PANORAMA_PRESET_ROTATION_DEG: SaunaRotationDeg =
	SAUNA_ROTATION_STEPS.find(
		(deg) => rotateAttachWall(TWO_ROOM_PANORAMA_WALL, deg) === "front",
	) ?? DEFAULT_SAUNA_ROTATION_DEG;

const PANORAMA_LOOK: TwoRoomPresetLook = {
	finishes: { exteriorTreatment: "slamfarg-black", interiorWood: "al" },
	timeOfDay: PANORAMA_PRESET_TIME_OF_DAY,
	deckAtGlassWall: true,
	roofConfig: PANORAMA_PRESET_ROOF,
	glassTowardWater: true,
};

/**
 * The default rectangular deck (2 m deep) along the panorama glass wall,
 * as wide as that wall. The glass wall is a SIDE wall, so its span is the
 * design's `length`, not the `width` the generic preset assumes.
 */
export function createGlassWallDeck(dimensions: SaunaDimensions): DeckConfig {
	return {
		...createDeckPreset("rectangular", dimensions, TWO_ROOM_PANORAMA_WALL),
		width: Math.round(dimensions.length * 10) / 10,
	};
}

export const TWO_ROOM_PRESET_LOOKS: Partial<
	Record<TwoRoomPresetId, TwoRoomPresetLook>
> = {
	lakeside: PANORAMA_LOOK,
	openPanorama: PANORAMA_LOOK,
};

interface PresetLookState {
	finishes: FinishConfig;
	timeOfDay: TimeOfDayValue;
	deckConfig: DeckConfig | null;
	roofConfig: RoofConfig;
	rotationDeg: SaunaRotationDeg;
}

function sameRoof(a: RoofConfig, b: RoofConfig): boolean {
	return (
		a.kind === b.kind &&
		a.pitchDeg === b.pitchDeg &&
		("systemId" in a ? a.systemId : undefined) ===
			("systemId" in b ? b.systemId : undefined)
	);
}

/**
 * The finishes, time of day, deck, roof and orientation after applying
 * `preset`'s look to the current values, touching only the ones still at
 * their defaults: a user who already chose a facade colour, a time of day,
 * a deck, a roof or turned the building keeps it.
 */
export function applyTwoRoomPresetLook(
	preset: TwoRoomPresetId,
	current: PresetLookState & { dimensions: SaunaDimensions },
): PresetLookState {
	const look = TWO_ROOM_PRESET_LOOKS[preset];
	if (!look) {
		return {
			finishes: current.finishes,
			timeOfDay: current.timeOfDay,
			deckConfig: current.deckConfig,
			roofConfig: current.roofConfig,
			rotationDeg: current.rotationDeg,
		};
	}
	const finishes = { ...current.finishes };
	for (const key of Object.keys(look.finishes) as Array<keyof FinishConfig>) {
		if (current.finishes[key] !== DEFAULT_FINISHES[key]) continue;
		Object.assign(finishes, { [key]: look.finishes[key] });
	}
	return {
		finishes,
		timeOfDay:
			current.timeOfDay === DEFAULT_TIME_OF_DAY
				? look.timeOfDay
				: current.timeOfDay,
		deckConfig:
			current.deckConfig === null && look.deckAtGlassWall
				? createGlassWallDeck(current.dimensions)
				: current.deckConfig,
		roofConfig:
			look.roofConfig &&
			sameRoof(current.roofConfig, DEFAULT_ROOF_CONFIG_BY_TYPE.tvarum)
				? look.roofConfig
				: current.roofConfig,
		rotationDeg:
			look.glassTowardWater &&
			current.rotationDeg === DEFAULT_SAUNA_ROTATION_DEG
				? PANORAMA_PRESET_ROTATION_DEG
				: current.rotationDeg,
	};
}

export function getMatchingTwoRoomPreset(
	config: TwoRoomConfig,
): TwoRoomPresetId | null {
	const entries = Object.entries(TWO_ROOM_PRESETS) as Array<
		[TwoRoomPresetId, TwoRoomConfig]
	>;

		return (
			entries.find(([, preset]) => {
				const saunaPositionMatches =
					!config.hasRelaxRoom || preset.saunaPosition === config.saunaPosition;

				return (
					preset.entryWall === config.entryWall &&
					preset.glazingStyle === config.glazingStyle &&
					saunaPositionMatches &&
					preset.hasRelaxRoom === config.hasRelaxRoom
				);
			})?.[0] ?? null
		);
}
